import { html } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";
import { SelectField } from "./fields.js";

/**
 * Base class for static select fields with predefined options
 * Extends SelectField to avoid API calls and use data from window.referentialData
 */
export class StaticSelectField extends SelectField {
	static properties = {
		nullOptionText: { type: String, attribute: "null-option-text" },
		nullOptionValue: { type: String, attribute: "null-option-value" },
		dataKey: { type: String, attribute: "data-key" },
		societyLegalForm: { type: Boolean, attribute: "data-society-legal-form", reflect: true },
		discardLegalForm: { type: Boolean, attribute: "discard-legal-form", reflect: true },
	};

	constructor() {
		super();
		this.nullOptionText = "Select an option";
		this.nullOptionValue = "0";
		this.societyLegalForm = false;
		this.discardLegalForm = false;
		// Subclasses should set this.dataKey to the table name
		this.dataKey = "";
	}

	/**
	 * Gets options from window.referentialData using the dataKey
	 * @returns Array of options with value and label
	 */
	getStaticOptions() {
		if (!this.dataKey || !window.referentialData) {
			return [];
		}
		
		const data = window.referentialData[this.dataKey];
		if (!data || !Array.isArray(data)) {
			return [];
		}
		
		// Map from backend format {value, name} to frontend format {value, label}
		return data.map(option => ({
			value: option.value,
			label: option.name
		}));
	}

	render() {
		const optionsStatic = Array.from(
			this.shadowRoot?.querySelectorAll("option-component") || []
		).map((option) => ({
			value: option.value,
			label: option.label,
		}));

		const options = [
			{ value: this.nullOptionValue, label: this.nullOptionText },
			...optionsStatic,
			...this.getStaticOptions(),
		];

		return html`
		  <div class="row align-items-center">
		    <label for="${this.idInput}" class="col-sm-3 col-form-label text-end">
		      ${this.label}
		    </label>
		    <div class="col-sm-9">
		      <select
		        id="${this.idInput}"
		        name="${this.name}"
		        .value="${this.value}"
		        @change="${this.handleChange}"
		        ?required=${this.required}
		      >
		        ${options.map(
					(option) =>
						html`<option
			        value="${option.value}"
			        ?selected="${this.value === option.value}"
			      >
			        ${option.label}
			      </option>`
				)}
		      </select>
		    </div>
		  </div>
		`;
	}

	getValues() {
		const staticOptions = this.getStaticOptions();
		const selected = staticOptions.find(
			(option) => this.value === option.value
		);
		const optionValue = selected ? selected.label : "";
		const result = { name: this.name, value: this.value, ...super.getValues() };
		result["optionValue"] = optionValue;
		
		// Include data-society-legal-form attribute if set
		if (this.societyLegalForm) {
			result["data-society-legal-form"] = "true";
		}
		
		// Include discard-legal-form attribute if set (excludes from price calculation)
		if (this.discardLegalForm) {
			result["discard-legal-form"] = "true";
		}
		
		return result;
	}
}

/**
 * Society Legal Form Select
 * Based on society_legal_form table
 */
export class SocietyLegalFormSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select with Directeur filter
 * Subset of society legal forms for directeur role (SA, SAS, SASU)
 */
export class SocietyLegalFormDirecteurSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_directeur";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select with Gerant filter
 * Subset of society legal forms for gerant role
 */
export class SocietyLegalFormGerantSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_gerant";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select with Administrateur filter
 * Subset of society legal forms for administrateur role (SA, SAS)
 */
export class SocietyLegalFormAdministrateurSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_administrateur";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select (Detailed with acronyms)
 * Full name with acronym in parentheses
 */
export class SocietyLegalFormDetailedSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_detailed";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select for SAS/SASU only
 */
export class SocietyLegalFormSasSasuSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_sas_sasu";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select for SARL only
 */
export class SocietyLegalFormSarlSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_sarl";
		this.societyLegalForm = true;
	}
}

/**
 * Society Legal Form Select for Civil Societies (SC, SCCV, SCEA, SCI)
 */
export class SocietyLegalFormCivilSocietySelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "society_legal_form_civil_society";
		this.societyLegalForm = true;
	}
}

/**
 * Decision Type Select
 * Based on decision_type table
 */
export class DecisionTypeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une nature de décision";
		this.dataKey = "decision_type";
	}
}

/**
 * Decision Making Body Select
 * Based on decision_making_body table
 */
export class DecisionBodySelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une qualité";
		this.dataKey = "decision_making_body";
	}
}

/**
 * Decision Making Body Modification Select
 * Based on decision_making_body_modification table
 */
export class DecisionBodyModificationSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une qualité";
		this.dataKey = "decision_making_body_modification";
	}
}

/**
 * Decision Type Select — TransfertSiegeMemeRessortSteCivileSteCommerciale
 * Includes "Décision" as the first option, followed by the standard types alphabetically.
 * Based on decision_type_transfert_siege data key.
 */
export class DecisionTypeTransfertSiegeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une nature de décision";
		this.dataKey = "decision_type_transfert_siege";
	}
}

/**
 * Decision Making Body Select — TransfertSiegeMemeRessortSteCivileSteCommerciale
 * L'actionnaire unique / Le président / Le gérant.
 * Based on decision_making_body_transfert_siege data key.
 */
export class DecisionBodyTransfertSiegeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une qualité";
		this.dataKey = "decision_making_body_transfert_siege";
	}
}

/**
 * Civil Society Type Select
 * Based on civil_society_type table
 */
export class CivilSocietyTypeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir un type de société civile";
		this.dataKey = "civil_society_type";
	}
}

/**
 * Tribunal Type Select
 * Based on opposition_tribunal_type table
 */
export class TribunalTypeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir un tribunal";
		this.dataKey = "opposition_tribunal_type";
	}
}

/**
 * Cessation Headquarters Type Select
 * Based on cessation_headquarters_type table
 */
export class CessationTypeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir un siège de la liquidation";
		this.dataKey = "cessation_headquarters_type";
	}
}

/**
 * President Type Select
 * Based on president_type table
 */
export class PresidentTypeSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Choisir une nature";
		this.dataKey = "president_type";
	}
}

/**
 * Immatriculation Legal Form Select
 * Based on legal_form table for immatriculation type 3 (EURL/SARLU/SARL/SELARL)
 * Uses legal_form_price for pricing
 */
export class ImmatriculationLegalFormSelect extends StaticSelectField {
	constructor() {
		super();
		this.nullOptionText = "Forme Juridique";
		this.dataKey = "legal_form_immatriculation";
	}
}

// Register all custom elements
customElements.define("society-legal-form-select", SocietyLegalFormSelect);
customElements.define("society-legal-form-detailed-select", SocietyLegalFormDetailedSelect);
customElements.define("society-legal-form-directeur-select", SocietyLegalFormDirecteurSelect);
customElements.define("society-legal-form-gerant-select", SocietyLegalFormGerantSelect);
customElements.define("society-legal-form-administrateur-select", SocietyLegalFormAdministrateurSelect);
customElements.define("society-legal-form-sas-sasu-select", SocietyLegalFormSasSasuSelect);
customElements.define("society-legal-form-sarl-select", SocietyLegalFormSarlSelect);
customElements.define("society-legal-form-civil-society-select", SocietyLegalFormCivilSocietySelect);
customElements.define("decision-type-select", DecisionTypeSelect);
customElements.define("decision-body-select", DecisionBodySelect);
customElements.define("decision-body-modification-select", DecisionBodyModificationSelect);
customElements.define("decision-type-transfert-siege-select", DecisionTypeTransfertSiegeSelect);
customElements.define("decision-body-transfert-siege-select", DecisionBodyTransfertSiegeSelect);
customElements.define("civil-society-type-select", CivilSocietyTypeSelect);
customElements.define("tribunal-type-select", TribunalTypeSelect);
customElements.define("cessation-type-select", CessationTypeSelect);
customElements.define("president-type-select", PresidentTypeSelect);
customElements.define("immatriculation-legal-form-select", ImmatriculationLegalFormSelect);
